import { action, makeObservable, observable } from "mobx";
import { DndOrderedDropFunction } from "../types";
import { createContext, useContext } from "react";

/** store handling drag and drop of ordered list */
export class DndOrderedStore {
	@observable.ref private _indices: number[];
	private readonly _onDrop?: DndOrderedDropFunction;
	private _size: number = 0;

	/** type of item being dragged; needs to be unique unless expecting items from other sources */
	readonly dndType: string;

	/** timeout used for throttling hover events (number) */
	readonly hoverThrottle: number;

	/** return current index for item at the given original index */
	getCurrentIndex = (originalIndex: number): number => {
		return this._indices?.indexOf(originalIndex) ?? originalIndex;
	};

	/** return original index for item currently at given index */
	getOriginalIndex = (index: number): number => {
		const { _indices } = this;
		return _indices?.[index] ?? index;
	};

	/** drag item at one index to another; if dropped, then onDrop callback is called */
	@action onDrag = (draggedOriginalIndex: number, overIndex: number): void => {
		const { getCurrentIndex, _size } = this;
		let { _indices } = this;

		if (_indices) {
			_indices = _indices.slice();
		} else {
			_indices = new Array(_size);

			for (let i = 0; i < _size; ++i) {
				_indices[i] = i;
			}
		}

		const index = getCurrentIndex(draggedOriginalIndex);
		_indices.splice(index, 1);
		_indices.splice(overIndex, 0, draggedOriginalIndex);
		this._indices = _indices;
	};

	/** cancel drag and drop */
	@action onDragCancel = (): void => {
		this._indices = null;
	};

	/** process drop of item dragged from original index */
	@action onDragComplete = (originalIndex: number): void => {
		const { getCurrentIndex, _onDrop } = this;
		const index = getCurrentIndex(originalIndex);
		_onDrop(originalIndex, index);
		this._indices = null;
	};

	/** increase size of array to given number (as you infinite scroll) */
	@action setSize = (size: number): void => {
		const { _indices, _size } = this;

		// no need to create _indices until drag and drop is used
		if (_indices) {
			if (size < _size) {
				// if size decreased, then assume new set of items in their original order (not dragging at the moment)
				this._indices = null;
			} else if (size > _size) {
				// if size increased, then all new items will have index matching their position in the list
				for (let i = _size; i < size; ++i) {
					_indices[i] = i;
				}
			}
		}

		this._size = size;
	};

	constructor(dndType: string, onDrop: DndOrderedDropFunction, hoverThrottle = 100) {
		this.dndType = dndType;
		this.hoverThrottle = hoverThrottle;
		this._onDrop = onDrop;
		makeObservable(this);
	}
}

/** context and hook to use store */
export const DndOrderedStoreContext = createContext<DndOrderedStore>(undefined);
export const useDndOrderedStore = (): DndOrderedStore => useContext(DndOrderedStoreContext);